import React, { useMemo } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { THEME_COLOR } from '../../../theme/formStyles';
import { UNASSIGNED, engineerKey, engineerName, isOnDay, isOpenVisit, itemKey, monthDays, siteColor, todayYmd, visitKey, visitMinutes } from '../projectSchedule.utils';

const cellBorder = { borderBottom: '1px solid #e5e9ee', borderRight: '1px solid #f0f2f5' };

// Fills a block with one colour per site; several sites on one day get equal diagonal bands
const blockBackground = (colors) => {
    if (colors.length === 1) return colors[0];
    const step = 100 / colors.length;
    return `linear-gradient(135deg, ${colors.map((c, i) => `${c} ${i * step}%, ${c} ${(i + 1) * step}%`).join(', ')})`;
};

const sameUser = (a, b) => !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();

// Block label: a live dot while on site, else hours from check-in/out, else the number of sub-tasks if several
const blockLabel = (dayTasks, dayVisits) => {
    if (dayVisits.some(isOpenVisit)) return '●';
    if (dayVisits.length) return `${Math.round(dayVisits.reduce((s, v) => s + visitMinutes(v), 0) / 60)}h`;
    return dayTasks.length > 1 ? dayTasks.length : '';
};

// Engineer × day grid for the month. Each block is one engineer-day, coloured by site.
export default function TeamBoard({ tasks, visits = [], year, month, onOpenDay }) {
    const days = useMemo(() => monthDays(year, month), [year, month]);
    const today = todayYmd();

    const rows = useMemo(() => {
        const byEngineer = new Map();
        tasks.forEach(t => {
            const key = engineerKey(t);
            if (!byEngineer.has(key)) byEngineer.set(key, { key, name: engineerName(t), tasks: [] });
            byEngineer.get(key).tasks.push(t);
        });
        return Array.from(byEngineer.values())
            .map(r => ({ ...r, busyDays: days.filter(d => r.tasks.some(t => isOnDay(t, d))).length }))
            // Unassigned work goes last so it reads as a to-do row
            .sort((a, b) => (a.key === UNASSIGNED) - (b.key === UNASSIGNED) || a.name.localeCompare(b.name));
    }, [tasks, days]);

    if (!rows.length) {
        return <Typography sx={{ textAlign: 'center', color: '#6b7785', fontSize: '13px', py: 4 }}>No sub-tasks scheduled this month.</Typography>;
    }

    return (
        <Box>
            <TableContainer sx={{ border: '1px solid #e5e9ee', borderRadius: 2, maxHeight: 640 }}>
                <Table size="small" stickyHeader sx={{ borderCollapse: 'separate' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...cellBorder, position: 'sticky', left: 0, zIndex: 3, bgcolor: '#fafbfc', minWidth: 170, fontWeight: 600, color: '#6b7785' }}>Engineer</TableCell>
                            {days.map(d => {
                                const dt = dayjs(d);
                                const isToday = d === today;
                                const weekend = dt.day() === 0 || dt.day() === 6;
                                return (
                                    <TableCell key={d} align="center" sx={{ ...cellBorder, p: '6px 0', minWidth: 34, fontSize: '12px', fontWeight: 500, bgcolor: isToday ? THEME_COLOR : '#fafbfc', color: isToday ? '#fff' : weekend ? '#b4bcc6' : '#6b7785', lineHeight: 1.2 }}>
                                        {dt.date()}<br /><span style={{ fontSize: '10px' }}>{dt.format('dd')[0]}</span>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(r => {
                            const isUa = r.key === UNASSIGNED;
                            return (
                                <TableRow key={r.key}>
                                    <TableCell sx={{ ...cellBorder, position: 'sticky', left: 0, zIndex: 1, bgcolor: '#fff', borderRight: '1px solid #e5e9ee' }}>
                                        <Typography sx={{ fontWeight: 500, fontSize: '13px', color: isUa ? '#dc2626' : '#1f2933' }}>{r.name}</Typography>
                                        <Typography sx={{ fontSize: '11px', color: '#6b7785' }}>
                                            {isUa ? 'needs an engineer' : `${r.busyDays} busy day${r.busyDays === 1 ? '' : 's'}`}
                                        </Typography>
                                    </TableCell>
                                    {days.map(d => {
                                        const dayTasks = r.tasks.filter(t => isOnDay(t, d));
                                        const colors = [...new Set(dayTasks.map(t => siteColor(t.facility_id)))];
                                        const taskKeys = new Set(dayTasks.map(itemKey));
                                        const dayVisits = isUa ? [] : visits.filter(v => v.visit_date === d && taskKeys.has(visitKey(v)) && sameUser(v.user_id, r.key));
                                        const weekend = [0, 6].includes(dayjs(d).day());
                                        return (
                                            <TableCell key={d} onClick={() => onOpenDay(d)}
                                                title={dayTasks.map(t => `${t.sub_task_name} (${t.facility_name})`).join('\n')}
                                                sx={{ ...cellBorder, p: 0, height: 46, position: 'relative', cursor: 'pointer', bgcolor: d === today ? '#f3fbf9' : weekend ? '#fcfcfd' : '#fff' }}>
                                                {dayTasks.length > 0 && (
                                                    <Box sx={{ position: 'absolute', inset: '5px 3px', borderRadius: '6px', display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 700, fontSize: '10.5px', background: blockBackground(colors), opacity: d < today ? 0.55 : 1 }}>
                                                        {blockLabel(dayTasks, dayVisits)}
                                                    </Box>
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Typography sx={{ fontSize: '12px', color: '#6b7785', mt: 1.25 }}>
                Each block is one engineer-day, coloured by site. Hours come from check-in/out; ● means on site now; a plain number means several sub-tasks with no check-in yet.
            </Typography>
        </Box>
    );
}
